import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Image as ImageIcon, Calendar, Info, Phone } from 'lucide-react';
import { publicApi, imageUrl } from '../api';
import { contactInfo } from '../mock';

const CONFIGS = {
  'weekly-menu': {
    eyebrow: 'Frisch, saisonal, hausgemacht', title: 'Wochenmenü', current: 'Aktuelles Wochenmenü',
    intro: 'Jede Woche stellen wir ein neues Wochenmenü zusammen — mit österreichischen Klassikern und saisonalen Highlights.',
    empty: 'Das Wochenmenü wird in Kürze hier veröffentlicht. Schauen Sie bald wieder vorbei.',
    note: 'Das Wochenmenü wird wöchentlich aktualisiert.',
  },
  'seasonal-menu': {
    eyebrow: 'Was die Saison zu bieten hat', title: 'Saisonkarte', current: 'Aktuelle Saisonkarte',
    intro: 'Unsere Saisonkarte bringt die besten Zutaten der Jahreszeit auf den Teller — regional und mit Liebe zubereitet.',
    empty: 'Die Saisonkarte wird in Kürze hier veröffentlicht. Schauen Sie bald wieder vorbei.',
    note: 'Die Saisonkarte wechselt mit den Jahreszeiten.',
  },
};

export default function WeeklyMenu() { return <MenuPage kind="weekly-menu"/>; }
export function SeasonalMenu() { return <MenuPage kind="seasonal-menu"/>; }

function MenuPage({ kind }) {
  const c = CONFIGS[kind];
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    publicApi.getMenu(kind)
      .then((m) => setMenu(m))
      .catch(() => setMenu(null))
      .finally(() => setLoading(false));
  }, [kind]);

  return (
    <div className="bg-[#faf6f0] pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="text-amber-600 tracking-[0.3em] text-xs uppercase">{c.eyebrow}</span>
          <h1 className="font-serif text-5xl md:text-6xl text-[#1a1410] mt-4">{c.title}</h1>
          <div className="w-16 h-[3px] bg-amber-500 mx-auto mt-6"/>
          <p className="text-[#5a4a3d] max-w-2xl mx-auto mt-6">
            {c.intro}
          </p>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-amber-100 p-12 text-center">
            <div className="animate-pulse text-[#5a4a3d]">Lädt …</div>
          </div>
        ) : menu ? (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-amber-100">
            <div className="bg-[#1a1410] text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar size={20} className="text-amber-400"/>
                <span className="font-serif text-lg">{c.current}</span>
              </div>
              <span className="text-xs text-amber-100/70">Aktualisiert: {new Date(menu.uploaded_at).toLocaleDateString('de-DE')}</span>
            </div>
            <div className="p-6 flex items-center justify-center bg-[#f3ebdd]">
              <img src={imageUrl(menu.image_url)} alt={c.title} data-testid="menu-image" className="max-w-full max-h-[80vh] rounded-lg shadow-lg"/>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-12 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mb-6">
              <ImageIcon size={36} className="text-amber-500"/>
            </div>
            <h3 className="font-serif text-2xl text-[#1a1410] mb-3">Noch kein Menü verfügbar</h3>
            <p className="text-[#5a4a3d] max-w-md mx-auto">
              {c.empty}
            </p>
          </div>
        )}

        <div className="mt-10 bg-gradient-to-br from-[#1a1410] to-[#2a1e15] rounded-2xl p-8 md:p-10 text-white shadow-xl">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <span className="text-amber-300 tracking-[0.3em] text-xs uppercase">Fragen zum Menü?</span>
              <h3 className="font-serif text-2xl md:text-3xl mt-2">Rufen Sie uns einfach an</h3>
              <p className="text-amber-50/80 mt-3 text-sm">Für Fragen zu Zutaten, Allergenen oder speziellen Wünschen sind wir telefonisch für Sie erreichbar.</p>
            </div>
            <div className="flex md:justify-end">
              <a href={`tel:${contactInfo.phone}`} className="inline-flex items-center gap-3 px-6 py-4 rounded-full bg-amber-500 hover:bg-amber-400 text-[#1a1410] font-bold text-lg transition-colors shadow-lg">
                <Phone size={22}/> {contactInfo.phone}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-4">
          <Info size={22} className="text-amber-600 shrink-0 mt-1"/>
          <div className="text-sm text-[#5a4a3d]">
            <strong className="text-[#1a1410]">Hinweis:</strong> {c.note} Bei Fragen zu Zutaten oder Allergenen sprechen Sie uns gerne persönlich oder telefonisch an.
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#5a4a3d] hover:text-amber-700">
            <Lock size={14}/> Admin-Bereich
          </Link>
        </div>
      </div>
    </div>
  );
}
